import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import _startCase from 'lodash/startCase';
import _toLower from 'lodash/toLower';

import { DateTime } from 'luxon';
import FormStepper from '../../components/FormStepper';
import ControlledForm from '../../components/ControlledForm';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';

import { getColor } from '../../core/colors';

import useNavigateWithConfirm from '../../core/hooks/useNavigateWithConfirm';
import { useGlobalFormState } from '../../core/context/GlobalFormStateContext';

import { FormValidationProvider } from '../../core/context/FormValidationContext';
import { FlightOperationNotification, FormGenerator } from '../../core/api/clients';
import { useFlightOperationNotificationData } from '../../core/context/FlightOperationNotificationDataContext';
import FlightOperationNotificationValidationSchema from './FlightOperationNotificationValidationSchema';
import EventStep from './FormSteps/EventStep';
import FlightStep from './FormSteps/FlightStep';
import NarrativeStep from './FormSteps/NarrativeStep';

function FlightOperationNotificationFormEdit(): JSX.Element {
  const authorizedFormData = useAuthorizedFormData();
  const { flightOperationNotification, update, updateStatus } = useFlightOperationNotificationData();
  const { setGlobalFormState } = useGlobalFormState();
  const [submitError, setSubmitError] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const navigateWithConfirm = useNavigateWithConfirm();

  const {
    formState: {
      isDirty,
      dirtyFields,
      ...formStateRest
    },
    ...formMethods
  } = useForm<FlightOperationNotification>({
    defaultValues: flightOperationNotification,
    mode: 'onSubmit',
    resolver: yupResolver(FlightOperationNotificationValidationSchema),
  });

  React.useEffect(() => {
    formMethods.reset(flightOperationNotification);
  }, [flightOperationNotification]);

  React.useEffect(() => {
    const { lastModifiedBy } = formMethods.getValues();
    const isSystemUser = lastModifiedBy === 'System';

    setGlobalFormState(isDirty, isSystemUser);
  }, [isDirty, formMethods]);

  React.useEffect(() => {
    const { lastModifiedBy, formGeneratedFrom } = formMethods.getValues();
    if (formGeneratedFrom === FormGenerator.Blank && lastModifiedBy === 'System') {
      formMethods.setValue('eventDate', DateTime.now().toISO());
    }
  }, []);
  const handleSave = () => update(formMethods.getValues());

  const onSubmit = () => {
    setSubmitError(false);
    update(formMethods.getValues(), () => {
      navigate(`/form/flight-operation-notification/${flightOperationNotification.id}/review`);
    });
  };

  const onError = () => {
    setSubmitError(true);
  };

  const isRevenueType = () => {
    const formValues = formMethods.getValues();
    return formValues.typeOfFlight === 'Revenue (part 121)';
  };

  return (
    <ControlledForm
      onSubmit={formMethods.handleSubmit(onSubmit, onError)}
      formProps={{
        noValidate: true,
      }}
    >
      <FormStepper
        control={formMethods.control}
        color={getColor(authorizedFormData.metadata?.color ?? 'grey')}
        steps={['Flight & Crew', 'Event', 'Narrative']}
        schema={FlightOperationNotificationValidationSchema.describe()}
        title="Flight Operation Notification"
        // eslint-disable-next-line max-len
        reportedBy={`${flightOperationNotification.employeeName} [${flightOperationNotification.employeeId}], ${_startCase(_toLower(flightOperationNotification.employeeTitle ?? ''))}, ${flightOperationNotification.employeeLocation}`}
        enableAutoSave={flightOperationNotification.lastModifiedBy !== 'System'}
        onSave={handleSave}
        saveStatus={updateStatus}
        lastSaved={flightOperationNotification.lastModifiedBy === 'System' ? null : flightOperationNotification.lastModifiedOn}
        isDirty={isDirty}
        onClose={() => navigateWithConfirm(authorizedFormData.metadata?.route ?? '')}
        submitError={submitError}
      >
        {({ activeStep }) => (
          <FormProvider formState={{ isDirty, dirtyFields, ...formStateRest }} {...formMethods}>
            <FormValidationProvider<FlightOperationNotification>>
              <FlightStep visible={activeStep === 0} isRevenue={isRevenueType()} />
              <EventStep visible={activeStep === 1} />
              <NarrativeStep visible={activeStep === 2} />
            </FormValidationProvider>
          </FormProvider>
        )}
      </FormStepper>
    </ControlledForm>
  );
}

export default FlightOperationNotificationFormEdit;
